/**
 * Shared catalog of recognized AI-assistant referrers + the public beacon
 * that fires when the current page-load's document.referrer hostname
 * matches one of the patterns.
 *
 * Why client-side detection (not GA4-only):
 *   - GA4 collapses ChatGPT/Perplexity/etc. into "Referral" by default;
 *     getting source-level breakdown takes custom event setup AND there's
 *     a 24-48h reporting delay.
 *   - This beacon writes straight into our Mongo, which the admin Analytics
 *     tab queries in sub-second. Closes the iteration loop on whether
 *     structured-data tweaks are actually moving the needle.
 *
 * Token allowlist below MUST stay in sync with KNOWN_AI_SOURCES in
 * /app/backend/server.py — the server soft-rejects any source it doesn't
 * recognize, so adding a new AI engine requires updating BOTH files.
 */

// Pattern: regex matchers the hostname portion of document.referrer is
// tested against, in order. First match wins. Order is more specific →
// more general so chat.openai.com hits "chatgpt" not a generic openai.com.
export const AI_SOURCE_PATTERNS = [
  { token: "chatgpt", re: /(^|\.)chatgpt\.com$|(^|\.)chat\.openai\.com$/i },
  { token: "openai", re: /(^|\.)openai\.com$/i },
  { token: "perplexity", re: /(^|\.)perplexity\.ai$/i },
  { token: "claude", re: /(^|\.)claude\.ai$/i },
  { token: "anthropic", re: /(^|\.)anthropic\.com$/i },
  { token: "gemini", re: /(^|\.)gemini\.google\.com$/i },
  { token: "googleai", re: /(^|\.)ai\.google$/i },
  // Bing's Copilot — referrer is bing.com/copilot or copilot.microsoft.com.
  { token: "copilot", re: /(^|\.)copilot\.microsoft\.com$/i },
  { token: "bing-copilot", re: /(^|\.)bing\.com$/i, pathRe: /copilot/i },
  { token: "you", re: /(^|\.)you\.com$/i },
  { token: "phind", re: /(^|\.)phind\.com$/i },
  { token: "kagi", re: /(^|\.)kagi\.com$/i },
  { token: "duckduckgo-ai", re: /(^|\.)duckduckgo\.com$/i, pathRe: /assist|chat/i },
  { token: "brave-leo", re: /(^|\.)search\.brave\.com$/i, pathRe: /leo|chat/i },
  { token: "poe", re: /(^|\.)poe\.com$/i },
  { token: "meta-ai", re: /(^|\.)meta\.ai$/i },
  { token: "mistral", re: /(^|\.)chat\.mistral\.ai$/i },
  { token: "deepseek", re: /(^|\.)chat\.deepseek\.com$/i },
  { token: "grok", re: /(^|\.)grok\.com$|(^|\.)x\.ai$/i },
];

// Friendly display labels for the admin dashboard. Same key set as
// AI_SOURCE_PATTERNS' tokens.
export const AI_SOURCE_LABELS = {
  chatgpt: "ChatGPT",
  openai: "OpenAI",
  perplexity: "Perplexity",
  claude: "Claude.ai",
  anthropic: "Anthropic",
  gemini: "Google Gemini",
  googleai: "Google AI",
  copilot: "Microsoft Copilot",
  "bing-copilot": "Bing Copilot",
  you: "You.com",
  phind: "Phind",
  kagi: "Kagi Assistant",
  "duckduckgo-ai": "DuckDuckGo AI",
  "brave-leo": "Brave Leo",
  poe: "Poe (Quora)",
  "meta-ai": "Meta AI",
  mistral: "Mistral Chat",
  deepseek: "DeepSeek",
  grok: "Grok",
};

/**
 * Inspect document.referrer, return the matching AI source token (or null).
 * Pure function — safe to unit-test or call from React effects.
 */
export function detectAiSource(referrer) {
  if (!referrer) return null;
  let host;
  let pathname;
  try {
    const u = new URL(referrer);
    host = u.hostname;
    pathname = u.pathname || "";
  } catch {
    return null;
  }
  for (const { token, re, pathRe } of AI_SOURCE_PATTERNS) {
    if (re.test(host)) {
      if (pathRe && !pathRe.test(pathname)) continue;
      return token;
    }
  }
  return null;
}

/**
 * Fire the beacon if (a) the referrer is an AI assistant and (b) we
 * haven't already fired it for this tab's session. Stores a sentinel in
 * sessionStorage so a user navigating across 10 pages of HavenCostGuide
 * counts as ONE AI-driven session, not ten.
 */
export async function maybeFireAiReferralBeacon(apiBase) {
  if (typeof window === "undefined") return;
  // Already fired this tab? Skip.
  try {
    if (window.sessionStorage.getItem("hcg_ai_beacon_fired") === "1") return;
  } catch {
    // sessionStorage disabled (private mode) — fall through and fire anyway
    // so we don't miss the data point.
  }
  const referrer = document.referrer || "";
  const source = detectAiSource(referrer);
  if (!source) return;
  try {
    window.sessionStorage.setItem("hcg_ai_beacon_fired", "1");
  } catch {
    // ignore
  }
  const payload = {
    source,
    referrer: referrer.slice(0, 500),
    landing_path: window.location.pathname + window.location.search,
    user_agent: (navigator.userAgent || "").slice(0, 500),
  };
  try {
    // navigator.sendBeacon when available — survives a fast page-unload and
    // doesn't block the main thread. Fallback to fetch with keepalive: true
    // (same survivability), then fetch (best effort).
    const url = `${apiBase}/api/analytics/ai-referral`;
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    });
  } catch {
    // Beacon failure is non-critical — don't surface to the user.
  }
}
